import { useEffect, useMemo, useRef, useState } from "react";
import { hsbToOklch, oklchToCss, oklchToHsb, type OklchColor } from "@/document/model/color";
import { colorAtFieldPoint, colorFieldState, colorFromField } from "./colorPickerModel";
import type { PickerMode } from "./types";

export function ColorField({
  mode,
  color,
  onChange,
  onChangeEnd,
}: {
  mode: PickerMode;
  color: OklchColor;
  onChange: (color: OklchColor) => void;
  onChangeEnd?: (color: OklchColor) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const latestColorRef = useRef(color);
  const draggingRef = useRef(false);
  const [localPoint, setLocalPoint] = useState<{ x: number; y: number } | undefined>(undefined);
  const field = colorFieldState(mode, color);
  const displayPoint = localPoint ?? { x: field.x, y: field.y };
  const cssBackground = useMemo(() => colorFieldBackground(mode, color), [color, mode]);

  useEffect(() => {
    latestColorRef.current = color;
    if (!draggingRef.current) {
      setLocalPoint(undefined);
    }
  }, [color]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || cssBackground) {
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const width = Math.max(1, Math.floor(rect.width));
    const height = Math.max(1, Math.floor(rect.height));
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }

    const image = context.createImageData(width, height);
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const colorAtPoint = colorAtFieldPoint(mode, color, x / Math.max(1, width - 1), y / Math.max(1, height - 1));
        const offset = (y * width + x) * 4;
        image.data[offset] = colorAtPoint.r;
        image.data[offset + 1] = colorAtPoint.g;
        image.data[offset + 2] = colorAtPoint.b;
        image.data[offset + 3] = 255;
      }
    }
    context.putImageData(image, 0, 0);
  }, [cssBackground, field.gradientKey, mode]);

  function pick(event: React.PointerEvent<HTMLDivElement>): OklchColor {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    setLocalPoint({ x: clamp01(x), y: clamp01(y) });
    const nextColor = colorFromField(mode, latestColorRef.current, x, y);
    latestColorRef.current = nextColor;
    onChange(nextColor);
    return nextColor;
  }

  return (
    <div
      className="relative mb-3 h-40 cursor-crosshair overflow-hidden rounded-md border border-black/40"
      style={{ background: cssBackground ?? undefined }}
      onPointerDown={event => {
        draggingRef.current = true;
        event.currentTarget.setPointerCapture(event.pointerId);
        pick(event);
      }}
      onPointerMove={event => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          pick(event);
        }
      }}
      onPointerUp={event => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
        draggingRef.current = false;
        setLocalPoint(undefined);
        onChangeEnd?.(latestColorRef.current);
      }}
      onPointerCancel={event => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
        draggingRef.current = false;
        setLocalPoint(undefined);
        onChangeEnd?.(latestColorRef.current);
      }}
    >
      {cssBackground ? null : <canvas ref={canvasRef} className="block h-full w-full" />}
      <div
        className="pointer-events-none absolute size-4 rounded-full"
        style={{
          left: `${displayPoint.x * 100}%`,
          top: `${displayPoint.y * 100}%`,
          transform: "translate(-50%, -50%)",
          boxShadow: "0 0 0 0.5px #fff, 0 0 0 1px #000",
        }}
      />
    </div>
  );
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
}

function colorFieldBackground(mode: PickerMode, color: OklchColor): string | undefined {
  if (mode === "hex" || mode === "hsb") {
    const hsb = oklchToHsb(color);
    const hue = oklchToCss(hsbToOklch({ h: hsb.h, s: 1, b: 1, alpha: 1 }));
    return [
      "linear-gradient(to top, #000 0%, rgba(0,0,0,0) 100%)",
      "linear-gradient(to right, #fff 0%, rgba(255,255,255,0) 100%)",
      hue,
    ].join(", ");
  }

  return undefined;
}
